/**
 * KAN-623: изменение профиля Telegram-аккаунта: имя, фамилия, "О себе",
 * аватар (загрузка JPEG/PNG до 5 МБ или удаление). Сохранение одним PATCH
 * multipart на {id}/profile/. Состояние формы живёт внутри DialogContent и
 * сбрасывается при закрытии. KAN-656: новое фото кадрируется в AvatarCropper,
 * в запрос уходит вырезанный квадрат JPEG.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AvatarCropper } from '@/components/telegram/AvatarCropper';
import { useAvatarSrc } from '@/hooks/useAvatarSrc';
import {
  cropToFile,
  outputSizeFor,
  type CropSelection,
} from '@/lib/avatarCrop';
import { describeApiError } from '@/lib/telegramConnect';
import {
  ABOUT_MAX,
  NAME_MAX,
  PROFILE_SAVED,
  RATE_WARNING,
  SAVE_ERROR,
  validatePhoto,
} from '@/lib/telegramProfile';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

interface Props {
  account: TelegramAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Текст ошибки: detail сервера, иначе общий разбор ошибок API. */
function errorText(err: unknown, fallback: string = SAVE_ERROR): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return describeApiError(err, fallback);
}

const LOAD_ERROR = 'Не удалось загрузить профиль из Telegram';

/** Профиль на аккаунте ещё не читали или он пуст: надо перечитать из Telegram. */
function needsProfileRefresh(account: TelegramAccount): boolean {
  if (!account.profile_updated_at) return true;
  return !(
    account.profile_first_name ||
    account.profile_last_name ||
    account.profile_about ||
    account.avatar_url
  );
}

type PrefillState =
  | { status: 'loading' }
  | { status: 'ready'; account: TelegramAccount }
  | { status: 'error'; error: string };

/**
 * При открытии диалога подтягивает профиль из Telegram (POST profile/),
 * если на аккаунте его нет. Иначе сразу берёт данные аккаунта.
 */
function useProfilePrefill(account: TelegramAccount): PrefillState {
  const queryClient = useQueryClient();
  const [state, setState] = useState<PrefillState>(() =>
    needsProfileRefresh(account)
      ? { status: 'loading' }
      : { status: 'ready', account }
  );
  const started = useRef(false);

  useEffect(() => {
    if (state.status !== 'loading' || started.current) return;
    started.current = true;
    let alive = true;
    telegramAccountsApi
      .refreshProfile(account.account_id, account.operator_id)
      .then((fresh) => {
        if (!alive) return;
        setState({ status: 'ready', account: { ...account, ...fresh } });
        void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      })
      .catch((err: unknown) => {
        if (alive) setState({ status: 'error', error: errorText(err, LOAD_ERROR) });
      });
    return () => {
      alive = false;
    };
  }, [account, queryClient, state.status]);

  return state;
}

function Form({
  account,
  onClose,
  loading = false,
}: {
  account: TelegramAccount;
  onClose: () => void;
  loading?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(account.profile_first_name ?? '');
  const [lastName, setLastName] = useState(account.profile_last_name ?? '');
  const [about, setAbout] = useState(account.profile_about ?? '');
  const [photo, setPhoto] = useState<File | null>(null);
  // Кадр нового фото; null, пока картинка не загрузилась в кроппер.
  const [crop, setCrop] = useState<CropSelection | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const currentSrc = useAvatarSrc(
    account.avatar_url ?? '',
    account.profile_updated_at ?? ''
  );
  const previewSrc = removePhoto ? null : currentSrc;
  const hasPhoto = Boolean(photo || previewSrc);

  const save = useMutation({
    mutationFn: async () => {
      const data = new FormData();
      data.append('first_name', firstName.trim());
      data.append('last_name', lastName.trim());
      data.append('about', about.trim());
      if (photo) {
        // Кадр в JPEG; пока картинка не загрузилась в кроппер, шлём исходник.
        data.append(
          'photo',
          crop
            ? await cropToFile(
                crop.image,
                crop.rect,
                outputSizeFor(crop.rect),
                photo.name
              )
            : photo
        );
      } else if (removePhoto) data.append('remove_photo', 'true');
      return telegramAccountsApi.updateProfile(
        account.account_id,
        data,
        account.operator_id
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      toast.success(PROFILE_SAVED);
      onClose();
    },
    onError: (err) => toast.error(errorText(err)),
  });

  const nameMissing = !firstName.trim();
  const canSave = !loading && !nameMissing && !photoError && !save.isPending;

  const onFile = (file: File | undefined): void => {
    if (!file) return;
    const error = validatePhoto(file);
    setPhotoError(error);
    setCrop(null);
    if (error) {
      setPhoto(null);
      return;
    }
    setPhoto(file);
    setRemovePhoto(false);
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      <div className="flex flex-wrap items-center gap-3">
        {photo ? (
          <AvatarCropper file={photo} onChange={setCrop} />
        ) : previewSrc ? (
          <img
            src={previewSrc}
            alt=""
            data-testid="edit-profile-preview"
            className="h-16 w-16 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            data-testid="edit-profile-no-photo"
            className="h-16 w-16 flex-shrink-0 rounded-full bg-gray-200"
          />
        )}
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            data-testid="edit-profile-photo-input"
            onChange={(e) => {
              onFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="mr-1 h-3.5 w-3.5" />
              Выбрать фото
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasPhoto}
              onClick={() => {
                setPhoto(null);
                setCrop(null);
                setPhotoError(null);
                setRemovePhoto(true);
              }}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Удалить фото
            </Button>
          </div>
          <span className="text-xs text-gray-500">JPEG или PNG, до 5 МБ</span>
          {photoError && (
            <span role="alert" className="text-xs text-red-600">
              {photoError}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="edit-profile-first-name">Имя</Label>
        <Input
          id="edit-profile-first-name"
          value={firstName}
          maxLength={NAME_MAX}
          disabled={loading}
          required
          onChange={(e) => setFirstName(e.target.value)}
        />
        {nameMissing && !loading && (
          <span className="text-xs text-red-600">Имя обязательно</span>
        )}
      </div>

      <div className="space-y-1">
        <Label htmlFor="edit-profile-last-name">Фамилия</Label>
        <Input
          id="edit-profile-last-name"
          value={lastName}
          maxLength={NAME_MAX}
          disabled={loading}
          onChange={(e) => setLastName(e.target.value)}
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label htmlFor="edit-profile-about">О себе</Label>
          <span
            className="text-xs text-gray-500"
            data-testid="edit-profile-about-counter"
          >
            {about.length}/{ABOUT_MAX}
          </span>
        </div>
        <Textarea
          id="edit-profile-about"
          value={about}
          maxLength={ABOUT_MAX}
          disabled={loading}
          rows={2}
          onChange={(e) => setAbout(e.target.value.slice(0, ABOUT_MAX))}
        />
      </div>

      <p className="text-xs text-gray-500">{RATE_WARNING}</p>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Отмена
        </Button>
        <Button type="submit" disabled={!canSave}>
          {save.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Сохранить
        </Button>
      </DialogFooter>
    </form>
  );
}

function PrefilledForm({
  account,
  onClose,
}: {
  account: TelegramAccount;
  onClose: () => void;
}): JSX.Element {
  const prefill = useProfilePrefill(account);
  return (
    <div className="space-y-3">
      {prefill.status === 'loading' && (
        <p
          className="flex items-center gap-2 text-sm text-gray-600"
          data-testid="edit-profile-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Загружаю профиль из Telegram...
        </p>
      )}
      {prefill.status === 'error' && (
        <p
          role="alert"
          className="text-sm text-red-600"
          data-testid="edit-profile-load-error"
        >
          {prefill.error}
        </p>
      )}
      <Form
        key={prefill.status}
        account={prefill.status === 'ready' ? prefill.account : account}
        loading={prefill.status === 'loading'}
        onClose={onClose}
      />
    </div>
  );
}

export function EditProfileDialog({
  account,
  open,
  onOpenChange,
}: Props): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Профиль Telegram</DialogTitle>
          <DialogDescription>
            {account.label || account.phone || account.account_id}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <PrefilledForm account={account} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
